// src/components/GoogleContacts.js 
import React, {
  useEffect,
  useMemo,
  useCallback,
  useState,
  forwardRef,
} from "react";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  Chip,
  Button,
  Tooltip,
  CircularProgress,
  MenuItem,
  Pagination,
  Stack,
  Link as MUILink,
  Drawer,
  Skeleton, // <--- ADDED SKELETON IMPORT
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CloudSync as CloudSyncIcon,
  Link as LinkIcon,
  Event as EventIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { motion } from "framer-motion";
import { trackedUrl } from "../utils/trackedLinks";
import api, { BASE_DOMAIN } from "../axiosConfig";

// ---------- Motion helpers ----------
const MotionBox = motion(Box);
const MotionRow = motion(TableRow);

// ---------- Debounce ----------
const useDebounce = (value, delay = 500) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return debounced;
};

// ---------- Snackbar MUI wrapper ----------
const SnackbarAlert = forwardRef(function SnackbarAlert(props, ref) {
  return <Alert elevation={6} ref={ref} variant="filled" {...props} />;
});

// ---------- Helpers ----------
const normalizePhones = (phones) => {
  if (!phones) return [];
  if (Array.isArray(phones)) return phones.filter(Boolean);
  return [];
};

const digitsOnly = (s) => (s || "").replace(/[^\d]/g, "");
const hasValidPhone = (list) =>
  normalizePhones(list).some((p) => digitsOnly(p).length >= 8);

const firstValidPhone = (list) => {
  const arr = normalizePhones(list);
  return arr.find((p) => digitsOnly(p).length >= 8) || null;
};

const buildGoogleCalendarLink = ({
  title,
  details,
  location,
  startISO,
  endISO,
  guestEmail,
}) => {
  const base = "https://calendar.google.com/calendar/u/0/r/eventedit";
  const params = new URLSearchParams();
  if (title) params.set("text", title);
  if (details) params.set("details", details);
  if (location) params.set("location", location);
  if (guestEmail) params.set("add", guestEmail);

  const start = startISO ? new Date(startISO) : new Date(Date.now() + 10 * 60 * 1000);
  const end = endISO ? new Date(endISO) : new Date(start.getTime() + 30 * 60 * 1000);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, "").replace(".000", "");
  params.set("dates", `${fmt(start)}/${fmt(end)}`);
  return `${base}?${params.toString()}`;
};

const connectHref = `${BASE_DOMAIN}/integrations/google/connect`; // (Option A)

/* =====================  NEW: Contacted helpers (surgical)  ===================== */
const hasBeenContacted = (p) =>
  Boolean(p?.last_contacted_at) ||
  (typeof p?.contact_interaction_count === "number" && p.contact_interaction_count > 0);

const formatRelative = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};
/* ============================================================================== */

// ---------- Right-side Contact Panel (Google-style card) ----------
function ContactSidePanel({ open, onClose, contact }) {
  // helpers (unchanged)
  const normalizePhones = (phones) => (Array.isArray(phones) ? phones.filter(Boolean) : []);
  const digitsOnly = (s) => (s || "").replace(/[^\d]/g, "");
  const firstValidPhone = (list) => {
    const arr = normalizePhones(list);
    return arr.find((p) => digitsOnly(p).length >= 8) || null;
  };

  const phones = React.useMemo(() => normalizePhones(contact?.phones), [contact]);
  const name =
    contact?.display_name ||
    [contact?.name_given, contact?.name_family].filter(Boolean).join(" ") ||
    contact?.company ||
    "(No name)";
  const initials = React.useMemo(() => {
    const parts = String(name).trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]?.toUpperCase() || "").join("");
  }, [name]);
  const mainPhone = firstValidPhone(phones);
  const mainEmail = contact?.primary_email || null;
  const company = contact?.company || "";

  // links (unchanged behavior)
  const calendarLink = buildGoogleCalendarLink({
    title: `Meeting with ${name}`,
    details: `Scheduled via Google Contacts`,
    location: company || "Online",
    guestEmail: mainEmail || undefined,
  });
  const whatsappDisabled = !mainPhone;
  const telDisabled = !mainPhone;
  const emailDisabled = !mainEmail;

  const telHref = mainPhone
    ? trackedUrl("tel", { to: mainPhone, contact_id: contact?.id, source: "google_sidepanel" })
    : undefined;
  const waHref = mainPhone
    ? trackedUrl("whatsapp", {
        to: mainPhone, contact_id: contact?.id, source: "google_sidepanel",
        text: "Hello! 👋 Can we chat about your inquiry?",
      })
    : undefined;
  const emailHref = mainEmail
    ? trackedUrl("email", {
        to: mainEmail, contact_id: contact?.id, source: "google_sidepanel",
        subject: `Hello ${name}`, body: "",
      })
    : undefined;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: 360, sm: 400, md: 440 },
          display: "flex",
          flexDirection: "column",
          borderLeft: (t) => `1px solid ${t.palette.divider}`,
          pt: { xs: 11, sm: 12 }, 
        },
      }}
    >
      <Box sx={{ px: 3, pb: 2, borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
        <Stack spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 72, height: 72, borderRadius: "50%", display: "grid", placeItems: "center",
              bgcolor: "success.light", color: "success.contrastText", fontWeight: 700,
              fontSize: 24, letterSpacing: 0.5, boxShadow: 1,
            }}
            aria-label="contact initials"
          >
            {initials || <PersonIcon fontSize="medium" />}
          </Box>

        <Typography variant="h6" fontWeight={700} textAlign="center">
            {name}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            {company ? (
              <>
                <BusinessIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                <Typography variant="caption" color="text.secondary">{company}</Typography>
              </>
            ) : null}
            <Chip size="small" label="Google" sx={{ ml: 1, bgcolor: "grey.100", borderRadius: 1.5 }} />
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ px: 2.5, py: 1.75, borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
          <Tooltip title={emailDisabled ? "No email" : "Email"}>
            <span>
              <Button size="small" variant="outlined" startIcon={<EmailIcon />}
                href={emailDisabled ? undefined : emailHref} target={emailDisabled ? undefined : "_blank"}
                rel="noopener noreferrer" disabled={emailDisabled}
                sx={{ borderRadius: 2, px: 1.25, bgcolor: "grey.50" }}>
                Email
              </Button>
            </span>
          </Tooltip>

          <Tooltip title={telDisabled ? "No phone" : "Call"}>
            <span>
              <Button size="small" variant="outlined" startIcon={<PhoneIcon />}
                href={telDisabled ? undefined : telHref} target={telDisabled ? undefined : "_blank"}
                rel="noopener noreferrer" disabled={telDisabled}
                sx={{ borderRadius: 2, px: 1.25, bgcolor: "grey.50" }}>
                Call
              </Button>
            </span>
          </Tooltip>

          <Tooltip title={whatsappDisabled ? "No phone" : "WhatsApp"}>
            <span>
              <Button size="small" variant="outlined" startIcon={<WhatsAppIcon />}
                href={whatsappDisabled ? undefined : waHref} target={whatsappDisabled ? undefined : "_blank"}
                rel="noopener noreferrer" disabled={whatsappDisabled}
                sx={{
                  borderRadius: 2, px: 1.25, bgcolor: "grey.50",
                  opacity: whatsappDisabled ? 0.35 : 1, filter: whatsappDisabled ? "grayscale(100%)" : "none",
                }}>
                WhatsApp
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="Schedule meeting">
            <span>
              <Button size="small" variant="contained" startIcon={<EventIcon />}
                href={calendarLink} target="_blank" rel="noopener noreferrer" sx={{ borderRadius: 2, px: 1.25 }}>
                Schedule
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      <Box sx={{ px: 3, py: 2.25, overflowY: "auto", flex: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Contact Details</Typography>

        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
          <EmailIcon sx={{ fontSize: 18, color: "text.disabled" }} />
          <Typography variant="body2" color={mainEmail ? "text.primary" : "text.disabled"}>
            {mainEmail || "—"}
          </Typography>
        </Stack>

        {phones.length ? (
          phones.map((ph) => (
            <Stack key={ph} direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.75 }}>
              <PhoneIcon sx={{ fontSize: 18, color: "text.disabled" }} />
              <Typography variant="body2">{ph}</Typography>
              <Chip size="small" label="Mobile" variant="outlined" sx={{ ml: 0.5, borderRadius: 1 }} />
            </Stack>
          ))
        ) : (
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.75 }}>
            <PhoneIcon sx={{ fontSize: 18, color: "text.disabled" }} />
            <Typography variant="body2" color="text.disabled">— none —</Typography>
          </Stack>
        )}

        <Button size="small" sx={{ mt: 1, textTransform: "none", px: 0 }}>
          + Add label
        </Button>
      </Box>

      <Box sx={{ px: 2, py: 1.25, borderTop: (t) => `1px solid ${t.palette.divider}`, display: "flex", justifyContent: "flex-end" }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: "none" }}>Close</Button>
      </Box>
    </Drawer>
  );
}

export default function GoogleContacts() {
  // data
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);

  // status / integration
  const [connected, setConnected] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  // ui state
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState({ open: false, type: "info", msg: "" });

  // filters/paging
  const [q, setQ] = useState(localStorage.getItem("gq") || "");
  const [label, setLabel] = useState(localStorage.getItem("glabel") || "");
  const [rows, setRows] = useState(Number(localStorage.getItem("grows")) || 25);
  // NOTE: state is 0-indexed for MUI, backend expects 1-indexed
  const [page, setPage] = useState(Number(localStorage.getItem("gpage")) || 0);

  // side panel state
  const [openPanel, setOpenPanel] = useState(false);
  const [activeContact, setActiveContact] = useState(null);

  const debouncedQ = useDebounce(q, 500);

  // ---------- connection status ----------
  const loadConnection = useCallback(async () => {
    try {
      const res = await api.get("/integrations/google/status");
      setConnected(!!res.data?.connected);
      setLastSyncedAt(
        res.data?.lastSyncedAt ?? res.data?.last_synced_at ?? null
      );
    } catch {
      console.warn("Could not load Google integration status");
    }
  }, []);

  // ---------- fetch contacts (paginated) ----------
  const loadContacts = useCallback(
    async (pg = 0, size = 25, query = "", lab = "") => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ limit: size });
        params.append("page", pg + 1); // Laravel paginate is 1-indexed
        if (query) params.append("q", query);
        if (lab) params.append("label", lab);

        const res = await api.get(`/google/contacts?${params.toString()}`);

        const items = res.data?.data || [];
        const ttl = res.data?.total ?? 0;

        setContacts(items);
        setTotal(ttl);
      } catch (e) {
        setError("Failed to fetch Google contacts.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ---------- trigger background sync ----------
  const triggerSync = useCallback(async () => {
    setSyncing(true);
    try {
      await api.post("/integrations/google/sync-now");
      setSnack({
        open: true,
        type: "success",
        msg: "Sync started. Refresh the list in a few seconds.",
      });
      loadConnection();
    } catch (e) {
      setSnack({ open: true, type: "error", msg: "Sync failed to start." });
    } finally {
      setSyncing(false);
    }
  }, [loadConnection]);

  // ---------- effects ----------
  useEffect(() => {
    loadConnection();
  }, [loadConnection]);

  useEffect(() => {
    loadContacts(page, rows, debouncedQ, label);
  }, [page, rows, debouncedQ, label, loadContacts]);

  // persist UI state
  useEffect(() => {
    localStorage.setItem("gq", q);
  }, [q]);
  useEffect(() => {
    localStorage.setItem("glabel", label);
  }, [label]);
  useEffect(() => {
    localStorage.setItem("grows", String(rows));
  }, [rows]);
  useEffect(() => {
    localStorage.setItem("gpage", String(page));
  }, [page]);

  // ---------- computed ----------
  const lastSyncText = useMemo(() => {
    if (!lastSyncedAt) return "Never synced";
    const d = new Date(lastSyncedAt);
    return `Last sync: ${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  }, [lastSyncedAt]);

  // ---------- handlers ----------
  const handlePageChange = (_evt, newPage1Indexed) => {
    setPage(newPage1Indexed - 1); // convert to 0-indexed
  };

  const handleRowsChange = (e) => {
    setRows(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (e) => {
    setQ(e.target.value);
    setPage(0);
  };

  const handleLabelChange = (e) => {
    setLabel(e.target.value);
    setPage(0);
  };

  const openSideFromRow = (contact) => {
    setActiveContact(contact);
    setOpenPanel(true);
  };

  const closeSide = () => {
    setOpenPanel(false);
    setActiveContact(null);
  };

  // ---------- render ----------
  return (
    <>
      <MotionBox
        key="google-contacts"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.35 }}
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <Paper
          sx={{
            p: { xs: 1, md: 3 },
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              flexWrap: "wrap",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h5" fontWeight={700}>
                Google Contacts
              </Typography>
              <Chip
                size="small"
                label={connected ? "Connected" : "Not connected"}
                color={connected ? "success" : "warning"}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {!connected && (
                <Tooltip title="Connect your Google account">
                  <span>
                    <Button
                      startIcon={<LinkIcon />}
                      variant="contained"
                      color="primary"
                      href={connectHref}
                    >
                      Connect
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Trigger on-demand sync">
                <span>
                  <Button
                    startIcon={<CloudSyncIcon />}
                    onClick={triggerSync}
                    disabled={!connected || syncing}
                    variant="outlined"
                  >
                    {syncing ? "Syncing..." : "Sync Now"}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Refresh list">
                <span>
                  <IconButton
                    onClick={() => loadContacts(page, rows, q, label)}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : <RefreshIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Typography
            variant="body2"
            sx={{ mb: 2, color: "text.secondary", flexShrink: 0 }}
          >
            {lastSyncText}
          </Typography>

          {/* Filters */}
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              flexWrap: "wrap",
              mb: 2,
              flexShrink: 0,
            }}
          >
            <TextField
              size="small"
              placeholder="Search name, email, phone…"
              value={q}
              onChange={handleFilterChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 260, flexGrow: 1 }}
            />
            <TextField
              size="small"
              select
              label="Label"
              value={label}
              onChange={handleLabelChange}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              <MenuItem value="Starred">Starred</MenuItem>
            </TextField>
            <TextField
              size="small"
              select
              label="Per Page"
              value={rows}
              onChange={handleRowsChange}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </TextField>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
              {error}
            </Alert>
          )}

          {/* Table */}
          <TableContainer sx={{ flexGrow: 1, overflowY: "auto" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name / Email</TableCell>
                  <TableCell>Phones</TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    Company
                  </TableCell>
                  {/* NEW: Last Contacted (desktop only) */}
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" }, width: 170 }}>
                    Last Contacted
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  // --- NEW SKELETON LOADER ---
                  [...Array(6)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                         <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Skeleton variant="circular" width={36} height={36} />
                            <Box>
                                <Skeleton variant="text" width={120} height={20} />
                                <Skeleton variant="text" width={160} height={15} />
                            </Box>
                         </Box>
                      </TableCell>
                      <TableCell><Skeleton width={100} height={32} sx={{ borderRadius: 2 }} /></TableCell>
                      <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}><Skeleton width={100} /></TableCell>
                      <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}><Skeleton width={80} /></TableCell>
                    </TableRow>
                  ))
                ) : contacts.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      align="center"
                      sx={{ p: 3, fontStyle: "italic" }}
                    >
                      No contacts found
                    </TableCell>
                  </TableRow>
                ) : (
                  contacts.map((p) => {
                    const phones = normalizePhones(p.phones);
                    const name =
                      p.display_name ||
                      [p.name_given, p.name_family].filter(Boolean).join(" ") ||
                      p.company ||
                      "(No name)";

                    const mainPhone = firstValidPhone(phones);
                    const whatsappDisabled = !hasValidPhone(phones);
                    const email = p.primary_email || null;

                    const telHref = mainPhone
                      ? trackedUrl("tel", {
                          to: mainPhone,
                          contact_id: p.id,
                          source: "google_table",
                        })
                      : undefined;

                    const waHref = mainPhone
                      ? trackedUrl("whatsapp", {
                          to: mainPhone,
                          contact_id: p.id,
                          source: "google_table",
                          text: "Hello! 👋 Can we chat about your inquiry?",
                        })
                      : undefined;

                    const emailHref = email
                      ? trackedUrl("email", {
                          to: email,
                          contact_id: p.id,
                          source: "google_table",
                          subject: "Hello from Abisiniya",
                          body: "Hi there...",
                        })
                      : undefined;

                    return (
                      <MotionRow
                        key={p.id || p.google_resource_name}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        hover
                        sx={{
                          position: "relative",
                          // IMPORTANT: no row onClick; panel only opens from "More"
                          "&:hover .gc-actions": {
                            opacity: 1,
                            transform: "translateY(0)",
                          },
                        }}
                      >
                        {/* Name / Email with contacted dot */}
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {hasBeenContacted(p) && (
                              <Box
                                title="Contacted"
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: "success.main",
                                  boxShadow: (t) => `0 0 0 2px ${t.palette.background.paper}`,
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap title={name}>
                                {name}
                              </Typography>
                              {email && (
                                <Typography variant="caption" color="text.secondary" noWrap title={email}>
                                  {email}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell sx={{ position: "relative" }}>
                          {phones.length === 0
                            ? "—"
                            : phones.slice(0, 3).map((ph) => (
                                <Chip
                                  key={ph}
                                  label={ph}
                                  size="small"
                                  sx={{ mr: 0.5, mb: 0.5 }}
                                />
                              ))}
                          {phones.length > 3 && (
                            <Chip label={`+${phones.length - 3}`} size="small" />
                          )}

                          {/* --- QUICK ACTIONS CAPSULE (hover) --- */}
                          
                          <Box
                            className="gc-actions"
                            sx={{
                              position: "absolute",
                              left: -250, 
                              mt: -2.5,
                              bgcolor: "success.main",
                              borderRadius: 999,
                              px: 1,
                              py: 0.25,
                              display: "inline-flex",
                              gap: 0.5,
                              boxShadow: 2,
                              opacity: 0,
                              transform: "translateY(4px)",
                              transition: "all 160ms ease",
                            }}
                          >
                            <Tooltip title={!mainPhone ? "No phone" : "Call"}>
                              <span>
                                <IconButton
                                  component="a"
                                  href={mainPhone ? telHref : undefined}
                                  target={mainPhone ? "_blank" : undefined}
                                  rel="noopener noreferrer"
                                  size="small"
                                  disabled={!mainPhone}
                                  sx={{
                                    color: "#fff",
                                    "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
                                  }}
                                >
                                  <PhoneIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip
                              title={whatsappDisabled ? "No phone" : "WhatsApp"}
                            >
                              <span>
                                <IconButton
                                  component="a"
                                  href={whatsappDisabled ? undefined : waHref}
                                  target={whatsappDisabled ? undefined : "_blank"}
                                  rel="noopener noreferrer"
                                  size="small"
                                  disabled={whatsappDisabled}
                                  sx={{
                                    color: "#fff",
                                    opacity: whatsappDisabled ? 0.35 : 1,
                                    filter: whatsappDisabled ? "grayscale(100%)" : "none",
                                    "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
                                  }}
                                >
                                  <WhatsAppIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title={!email ? "No email" : "Email"}>
                              <span>
                                <IconButton
                                  component="a"
                                  href={email ? emailHref : undefined}
                                  target={email ? "_blank" : undefined}
                                  rel="noopener noreferrer"
                                  size="small"
                                  disabled={!email}
                                  sx={{
                                    color: "#fff",
                                    "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
                                  }}
                                >
                                  <EmailIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>

                            {/* More (opens side panel) */}
                            <Tooltip title="More">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openSideFromRow(p);
                                  }}
                                  sx={{
                                    color: "#fff",
                                    "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
                                  }}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>

                            {/* Schedule meeting */}
                            <Tooltip title="Schedule meeting">
                              <span>
                                <IconButton
                                  size="small"
                                  component="a"
                                  href={buildGoogleCalendarLink({
                                    title: `Meeting with ${name}`,
                                    details: `Scheduled via Google Contacts panel`,
                                    location: p.company || "Online",
                                    guestEmail: email || undefined,
                                  })}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    color: "#fff",
                                    "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
                                  }}
                                >
                                  <EventIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>

                        <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                          {p.company || "—"}
                        </TableCell>

                        {/* NEW: Last Contacted (desktop only) */}
                        <TableCell sx={{ display: { xs: "none", md: "table-cell" }, whiteSpace: "nowrap" }}>
                          {p.last_contacted_at ? (
                            <Tooltip title={new Date(p.last_contacted_at).toLocaleString()}>
                              <span>{formatRelative(p.last_contacted_at)}</span>
                            </Tooltip>
                          ) : hasBeenContacted(p) ? (
                            <Chip size="small" label="Contacted" />
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                      </MotionRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 2,
              flexShrink: 0,
              pt: 1,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Pagination
              count={Math.max(1, Math.ceil(total / rows))}
              page={page + 1}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              disabled={loading}
            />
          </Box>

          {/* Small hint */}
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Tip: Use the ⋮ <strong>More</strong> on each row to open the contact side panel.{" "}
              <MUILink
                href="https://calendar.google.com/"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                Meetings open in Google Calendar.
              </MUILink>
            </Typography>
          </Box>
        </Paper>
      </MotionBox>

      {/* Right-side panel (opens only from More) */}
      <ContactSidePanel open={openPanel} onClose={closeSide} contact={activeContact} />

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <SnackbarAlert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.type}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </SnackbarAlert>
      </Snackbar>
    </>
  );
}
